import { STeams } from "../../ExternalAPI/STeams";
import { UseCase } from "../TypesAndInterfaces/UseCase";
import { app } from "../../index";
import { OrderStatusId } from "../Domain/Order";

interface readOrdersByParamsUseCaseOptions {
  statusId: string,
  page: number;
  pageSize: number;
  partnersKey: string;
  continuationToken?: string | null;
}

export type readOrdersByParamsResult = {
  orders: any[];
  page: number;
  pageSize: number;
  statusId: string;
  continuationToken?: string;
  hasMore: boolean;
}

async function action(options: readOrdersByParamsUseCaseOptions): Promise<readOrdersByParamsResult> {
  // Temporary condition for StandBy orders
  if (options.statusId === OrderStatusId.SentForManufacturing) {
    const [manufacturingResult, standByResult] = await Promise.all([
      STeams.getOrdersByPartner({
        partnersKey: options.partnersKey,
        page: 1,
        pageSize: 10000,
        statusId: OrderStatusId.SentForManufacturing
      }),
      STeams.getOrdersByPartner({
        partnersKey: options.partnersKey,
        page: 1,
        pageSize: 10000,
        statusId: OrderStatusId.StandBy
      })
    ]);

    // Extract orders from the response objects
    const manufacturingOrders = Array.isArray(manufacturingResult) ? manufacturingResult : manufacturingResult.orders;
    const standByOrders = Array.isArray(standByResult) ? standByResult : standByResult.orders;

    const mergedOrders = [...manufacturingOrders, ...standByOrders].sort((a, b) => {
      const dateA = new Date(a.order.createdOn).getTime();
      const dateB = new Date(b.order.createdOn).getTime();
      return dateB - dateA;
    });

    const startIdx = (options.page - 1) * options.pageSize;
    const endIdx = startIdx + options.pageSize;

    const paginatedOrders: any = mergedOrders.slice(startIdx, endIdx);

    return {
      orders: paginatedOrders,
      page: options.page,
      pageSize: options.pageSize,
      statusId: OrderStatusId.SentForManufacturing,
      hasMore: endIdx < mergedOrders.length
    };
  }

  // Use the updated API with continuation token support
  const result = await STeams.getOrdersByPartner({
    partnersKey: options.partnersKey,
    page: options.page,
    pageSize: options.pageSize,
    statusId: options.statusId,
    continuationToken: options.continuationToken || null
  });

  // Handle both response formats (array or object with orders property)
  const orders = Array.isArray(result) ? result : result.orders;
  const continuationToken = Array.isArray(result) ? null : result.continuationToken;
  const hasMore = Array.isArray(result) ? false : (result.hasMore || !!result.continuationToken);

  return { 
    orders: orders || [],
    page: options.page,
    pageSize: options.pageSize,
    statusId: options.statusId,
    continuationToken,
    hasMore
  };
}

export const readOrdersByParamsUseCase: UseCase = {
  type: 'readOrdersByParams',
  run: action
};

export async function readOrdersByParams(options: readOrdersByParamsUseCaseOptions) {
  await app.runUseCase(readOrdersByParamsUseCase, options);
}